import {
  DEFAULT_PRESENTATION_BY_TYPE,
  REGISTRY_BLOCK,
  type RegistryBlockKey,
  type RegistryBlockPresentation,
  type RegistryBlockType,
  type ThemeId,
} from '../catalog/platformCatalogIds';
import { LAYOUT_COMPONENT_TYPE, type LayoutComponentType } from '../content/studio';
import { getThemeRegistryEntry } from '../theme/themeResolve';

const P = REGISTRY_BLOCK.presentation;

/** Canonical presentation per canvas component type (fallback when no registryBlockKey). */
export const COMPONENT_TYPE_REGISTRY_KEYS = {
  [LAYOUT_COMPONENT_TYPE.navigationHeader]: P.navigationRail,
  [LAYOUT_COMPONENT_TYPE.heroEditorialSplit]: P.heroEditorialSplit,
  [LAYOUT_COMPONENT_TYPE.menuGrid3Column]: P.staticMenuGrid,
  [LAYOUT_COMPONENT_TYPE.dynamicMenuFlow]: P.dynamicMenuFlow,
  [LAYOUT_COMPONENT_TYPE.reservationFormEmbed]: P.reservationFormEmbed,
  [LAYOUT_COMPONENT_TYPE.reservationThirdPartyBook]: P.reservationThirdPartyBook,
  [LAYOUT_COMPONENT_TYPE.reservationMessengerBook]: P.reservationMessengerBook,
  [LAYOUT_COMPONENT_TYPE.reservationCallBook]: P.reservationCallBook,
  [LAYOUT_COMPONENT_TYPE.chefStoryBlock]: P.chefStoryBlock,
  [LAYOUT_COMPONENT_TYPE.kitchenTeamBlock]: P.kitchenTeamLead,
  [LAYOUT_COMPONENT_TYPE.imageGallery]: P.imageGalleryMosaic,
  [LAYOUT_COMPONENT_TYPE.tastingMenuCourseTimeline]: P.tastingMenuCourseTimeline,
  [LAYOUT_COMPONENT_TYPE.allergyCaptureSecureForm]: P.allergyCaptureSecureForm,
  [LAYOUT_COMPONENT_TYPE.heroVideoAtmosphere]: P.heroVideoAtmosphere,
  [LAYOUT_COMPONENT_TYPE.footerStandard]: P.footerStandard,
  [LAYOUT_COMPONENT_TYPE.legalImprintBlock]: P.legalImprintBlock,
  [LAYOUT_COMPONENT_TYPE.legalPrivacyBlock]: P.legalPrivacyBlock,
  [LAYOUT_COMPONENT_TYPE.simpleOpeningHours]: P.simpleOpeningHours,
  [LAYOUT_COMPONENT_TYPE.addressMapBlock]: P.addressMapBlock,
  [LAYOUT_COMPONENT_TYPE.drinksListStatic]: P.drinksListStatic,
  [LAYOUT_COMPONENT_TYPE.staticLocationText]: P.staticLocationText,
  [LAYOUT_COMPONENT_TYPE.simpleMenuStrip]: P.simpleMenuStrip,
  [LAYOUT_COMPONENT_TYPE.liveBakeryCounterList]: P.liveBakeryCounterList,
  [LAYOUT_COMPONENT_TYPE.socialFeedGrid]: P.socialFeedGrid,
  [LAYOUT_COMPONENT_TYPE.coffeeSubscriptionMatrix]: P.coffeeSubscriptionMatrix,
  [LAYOUT_COMPONENT_TYPE.preOrderPickupForm]: P.preOrderPickupForm,
  [LAYOUT_COMPONENT_TYPE.dynamicCocktailSpotlight]: P.dynamicCocktailSpotlight,
  [LAYOUT_COMPONENT_TYPE.privatePartyInquiryForm]: P.privatePartyInquiryForm,
  [LAYOUT_COMPONENT_TYPE.liveTapListSync]: P.liveTapListSync,
  [LAYOUT_COMPONENT_TYPE.vipTableBookingStrip]: P.vipTableBookingStrip,
  [LAYOUT_COMPONENT_TYPE.weeklyScheduleRouteStrip]: P.weeklyScheduleRouteStrip,
  [LAYOUT_COMPONENT_TYPE.cateringInquiryForm]: P.cateringInquiryForm,
  [LAYOUT_COMPONENT_TYPE.liveGpsLocationEmbed]: P.liveGpsLocationEmbed,
  [LAYOUT_COMPONENT_TYPE.popUpTicketPurchaseCard]: P.popUpTicketPurchaseCard,
  [LAYOUT_COMPONENT_TYPE.contactBlock]: P.contactVisit,
  [LAYOUT_COMPONENT_TYPE.faqBlock]: P.faqAccordion,
  [LAYOUT_COMPONENT_TYPE.backToTopFloat]: P.backToTopCorner,
  [LAYOUT_COMPONENT_TYPE.contactChannelsFloat]: P.contactChannelsRail,
  [LAYOUT_COMPONENT_TYPE.utilityBar]: P.utilityBarSticky,
  [LAYOUT_COMPONENT_TYPE.announcementBar]: P.announcementBarBand,
} as const satisfies Record<LayoutComponentType, RegistryBlockKey>;

/** Presentations that share a canvas `componentType` with another key. */
const REGISTRY_BLOCK_COMPONENT_ALIASES: Partial<Record<RegistryBlockKey, LayoutComponentType>> = {
  [P.navigationRail]: LAYOUT_COMPONENT_TYPE.navigationHeader,
  [P.navigationClassic]: LAYOUT_COMPONENT_TYPE.navigationHeader,
  [P.navigationOverlay]: LAYOUT_COMPONENT_TYPE.navigationHeader,
  [P.navigationQuiet]: LAYOUT_COMPONENT_TYPE.navigationHeader,
  [P.navigationFrosted]: LAYOUT_COMPONENT_TYPE.navigationHeader,
  [P.navigationConversion]: LAYOUT_COMPONENT_TYPE.navigationHeader,
  [P.navigationWhisper]: LAYOUT_COMPONENT_TYPE.navigationHeader,
  [P.navigationEditorial]: LAYOUT_COMPONENT_TYPE.navigationHeader,
  [P.navigationCompass]: LAYOUT_COMPONENT_TYPE.navigationHeader,
  [P.navigationCurtain]: LAYOUT_COMPONENT_TYPE.navigationHeader,
  [P.navigationLedger]: LAYOUT_COMPONENT_TYPE.navigationHeader,
  [P.navigationHarbor]: LAYOUT_COMPONENT_TYPE.navigationHeader,
  [P.navigationMonogram]: LAYOUT_COMPONENT_TYPE.navigationHeader,
  [P.navigationSignal]: LAYOUT_COMPONENT_TYPE.navigationHeader,
  [P.navigationGallery]: LAYOUT_COMPONENT_TYPE.navigationHeader,
  [P.navigationHeader]: LAYOUT_COMPONENT_TYPE.navigationHeader,
  [P.heroCoffeeSpotlight]: LAYOUT_COMPONENT_TYPE.heroEditorialSplit,
  [P.heroFullBleedImage]: LAYOUT_COMPONENT_TYPE.heroEditorialSplit,
  [P.heroStandard]: LAYOUT_COMPONENT_TYPE.heroEditorialSplit,
  [P.heroQuietLuxury]: LAYOUT_COMPONENT_TYPE.heroEditorialSplit,
  [P.heroCounterBleed]: LAYOUT_COMPONENT_TYPE.heroEditorialSplit,
  [P.heroUtilityReservation]: LAYOUT_COMPONENT_TYPE.heroEditorialSplit,
  [P.heroCollageStory]: LAYOUT_COMPONENT_TYPE.heroEditorialSplit,
  [P.heroReelFade]: LAYOUT_COMPONENT_TYPE.heroEditorialSplit,
  [P.staticLocationStop]: LAYOUT_COMPONENT_TYPE.staticLocationText,
  [P.simpleMenuBoard]: LAYOUT_COMPONENT_TYPE.simpleMenuStrip,
  [P.weeklyScheduleRouteBoard]: LAYOUT_COMPONENT_TYPE.weeklyScheduleRouteStrip,
  [P.heroBrothSteam]: LAYOUT_COMPONENT_TYPE.heroEditorialSplit,
  [P.staticMenuPdfCard]: LAYOUT_COMPONENT_TYPE.menuGrid3Column,
  [P.staticMenuCategoryGallery]: LAYOUT_COMPONENT_TYPE.menuGrid3Column,
  [P.staticMenuDishCards]: LAYOUT_COMPONENT_TYPE.menuGrid3Column,
  [P.staticMenuEditorialBoard]: LAYOUT_COMPONENT_TYPE.menuGrid3Column,
  [P.staticMenuStreetBoard]: LAYOUT_COMPONENT_TYPE.menuGrid3Column,
  [P.staticMenuCategoryChips]: LAYOUT_COMPONENT_TYPE.menuGrid3Column,
  [P.staticMenuHighlightStrip]: LAYOUT_COMPONENT_TYPE.menuGrid3Column,
  [P.staticMenuHighlightMarquee]: LAYOUT_COMPONENT_TYPE.menuGrid3Column,
  [P.staticMenuHighlightHeroPlate]: LAYOUT_COMPONENT_TYPE.menuGrid3Column,
  [P.staticMenuHighlightZine]: LAYOUT_COMPONENT_TYPE.menuGrid3Column,
  [P.staticMenuHighlightChalkboard]: LAYOUT_COMPONENT_TYPE.menuGrid3Column,
  [P.staticMenuHighlightChefsCut]: LAYOUT_COMPONENT_TYPE.menuGrid3Column,
  [P.staticMenuHighlightTodaySplit]: LAYOUT_COMPONENT_TYPE.menuGrid3Column,
  [P.staticMenuHighlightPriceTiles]: LAYOUT_COMPONENT_TYPE.menuGrid3Column,
  [P.staticMenuHighlightDishCards]: LAYOUT_COMPONENT_TYPE.menuGrid3Column,
  [P.footerColophon]: LAYOUT_COMPONENT_TYPE.footerStandard,
  [P.footerDirectory]: LAYOUT_COMPONENT_TYPE.footerStandard,
  [P.footerUtility]: LAYOUT_COMPONENT_TYPE.footerStandard,
  [P.reservationEditorialForm]: LAYOUT_COMPONENT_TYPE.reservationFormEmbed,
  [P.chefStoryQuoteLed]: LAYOUT_COMPONENT_TYPE.chefStoryBlock,
  [P.chefStoryKitchenStrip]: LAYOUT_COMPONENT_TYPE.chefStoryBlock,
  [P.chefStoryNotes]: LAYOUT_COMPONENT_TYPE.chefStoryBlock,
  [P.kitchenTeamRow]: LAYOUT_COMPONENT_TYPE.kitchenTeamBlock,
  [P.kitchenTeamBand]: LAYOUT_COMPONENT_TYPE.kitchenTeamBlock,
  [P.imageGalleryMasonry]: LAYOUT_COMPONENT_TYPE.imageGallery,
  [P.imageGalleryFilmstrip]: LAYOUT_COMPONENT_TYPE.imageGallery,
  [P.imageGallerySpotlight]: LAYOUT_COMPONENT_TYPE.imageGallery,
  [P.imageGalleryLedger]: LAYOUT_COMPONENT_TYPE.imageGallery,
  [P.contactDesk]: LAYOUT_COMPONENT_TYPE.contactBlock,
  [P.contactLedger]: LAYOUT_COMPONENT_TYPE.contactBlock,
  [P.contactThreshold]: LAYOUT_COMPONENT_TYPE.contactBlock,
  [P.faqLedger]: LAYOUT_COMPONENT_TYPE.faqBlock,
  [P.backToTopPill]: LAYOUT_COMPONENT_TYPE.backToTopFloat,
  [P.contactChannelsFab]: LAYOUT_COMPONENT_TYPE.contactChannelsFloat,
  [P.utilityBarStatic]: LAYOUT_COMPONENT_TYPE.utilityBar,
  [P.utilityBarSticky]: LAYOUT_COMPONENT_TYPE.utilityBar,
  [P.utilityBarCarousel]: LAYOUT_COMPONENT_TYPE.utilityBar,
  [P.utilityBarCollapse]: LAYOUT_COMPONENT_TYPE.utilityBar,
  [P.announcementBarBand]: LAYOUT_COMPONENT_TYPE.announcementBar,
  [P.drinksListCellar]: LAYOUT_COMPONENT_TYPE.drinksListStatic,
  [P.drinksListSheet]: LAYOUT_COMPONENT_TYPE.drinksListStatic,
  [P.privatePartyInquirySalon]: LAYOUT_COMPONENT_TYPE.privatePartyInquiryForm,
  [P.tastingMenuPairingCards]: LAYOUT_COMPONENT_TYPE.tastingMenuCourseTimeline,
};

const CANONICAL_REGISTRY_BY_COMPONENT = new Map<RegistryBlockKey, LayoutComponentType>(
  Object.entries(COMPONENT_TYPE_REGISTRY_KEYS).map(([componentType, registryKey]) => [
    registryKey,
    componentType as LayoutComponentType,
  ]),
);

/** Resolves the canvas `componentType` for a presentation registry key. */
export function resolveComponentTypeForRegistryBlock(
  registryKey: RegistryBlockKey,
): LayoutComponentType | undefined {
  return (
    CANONICAL_REGISTRY_BY_COMPONENT.get(registryKey) ??
    REGISTRY_BLOCK_COMPONENT_ALIASES[registryKey]
  );
}

export function hasBlockImplementation(registryKey: RegistryBlockKey): boolean {
  return resolveComponentTypeForRegistryBlock(registryKey) !== undefined;
}

/**
 * Theme-curated presentation for a block type, with type default fallback.
 * Only returns a presentation when the type is known.
 */
export function resolvePresentationForBlockType(
  blockType: RegistryBlockType,
  themeId?: string,
): RegistryBlockPresentation {
  if (themeId) {
    const entry = getThemeRegistryEntry(themeId);
    const fromTheme = entry?.blockPresentations?.[blockType];
    if (fromTheme) {
      return fromTheme;
    }
  }
  return DEFAULT_PRESENTATION_BY_TYPE[blockType];
}

export type { ThemeId };
